/** Convert any API-provided value (strings, arrays, objects/dictionaries) into a trimmed string. */
const toStringSafe = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v.trim();
    if (Array.isArray(v)) return v.map(toStringSafe).filter(Boolean).join(' ');
    if (typeof v === 'object') return Object.values(v).map(toStringSafe).filter(Boolean).join(' ');
    return String(v).trim();
};

/** Canonicalize severity levels. */
const normalizeLevel = (lvl) => {
    const s = String(lvl || '').toLowerCase();
    if (s === 'error') return 'error';
    if (s === 'warning' || s === 'warn') return 'warning';
    return s || 'info';
};

/** Normalize and de-duplicate issues by (level|message) without using iterator syntax. */
const dedupeNormalizedIssues = (arr) => {
    const seen = Object.create(null);
    const list = Array.isArray(arr) ? arr : [];

    return list.reduce((out, it) => {
        const level = normalizeLevel((it && (it.severity ?? it.level)) || '');
        const message = toStringSafe(it && (it.message ?? it.issueCode ?? ''));
        const value = toStringSafe(it && it.value);

        const displayMessage = message && value ? `${message} ${value}` : message || value;
        if (!displayMessage) return out;

        const key = `${level}|${displayMessage}`;
        if (seen[key]) return out;

        seen[key] = true;
        out.push({ level, message: displayMessage });
        return out;
    }, []);
};

export default function adaptApiInvoiceToUi(api, opts = {}) {
    const { forcePreInvoicedLabel = false } = opts;
    if (!api) return null;

    // --- pull from API with safe defaults ---
    const {
        id: apiId,
        fileName,
        customerId,
        clientName,
        invoiceNumber,
        clinicianName,
        numberOfLocations,
        bookingIds = [],
        invoiceDate,
        accountingDate,
        status,
        invoiceType,
        totalInvoiceAmount,
        netInvoiceAmount,
        totalTax,
        totalDiscount,
        amountDue,
        isPreInvoice,
        isCorrection,
        hasErrors,
        hasWarnings,
        invoiceIssues = [],
        timesheets = [],
        expenses = [], // grouped
        deposits = [],
        selectable,
        isHighPriority,
        collectorName,
        collectorSfId,
    } = api || {};

    const bookingId = apiId; // treat invoice.id as bookingId for selection
    const rawStatus = typeof status === 'string' ? status.trim() : '';
    const displayStatus =
        forcePreInvoicedLabel && rawStatus === 'Invoiced' ? 'PreInvoiced' : rawStatus;
    // ---------------- Timesheets (parent with sub-items: lineItems) ----------------
    const uiTimesheets = (Array.isArray(timesheets) ? timesheets : []).map((ts) => ({
        // fields exactly as in the API payload
        id: ts?.processedTimesheetId,
        weekEnding: ts?.weekEnding,
        numberOfLocations: ts?.numberOfLocations,
        bookingId: ts?.bookingId,
        timesheetId: ts?.timesheetId,
        processedTimesheetId: ts?.processedTimesheetId,
        clinicianId: ts?.clinicianId,
        totals: ts?.totals,
        locationsCount: ts?.numberOfLocations,
        isHighPriority:
            Array.isArray(ts?.groupedTimesheets) &&
            ts.groupedTimesheets.some(
                (g) =>
                    !!g?.isHighPriority ||
                    (Array.isArray(g?.lineItems) && g.lineItems.some((it) => !!it?.isHighPriority))
            ),
        groupedTimesheets: ts?.groupedTimesheets,
    }));

    // ---------------- Deposits (leaf items) ----------------
    const uiDeposits = (Array.isArray(deposits) ? deposits : []).map((d) => ({
        id: d?.id,
        billAmount: Number(d?.billAmount ?? 0),
        amount: Number(d?.billAmount ?? 0),
    }));

    // ---------------- Expenses (grouped shape ONLY after revert) ----------------
    const apiExpenseGroups = Array.isArray(expenses) ? expenses : [];
    const expensesGroups = apiExpenseGroups.map((group) => {
        const items = Array.isArray(group?.items) ? group.items : [];
        const groupIsCorrection = !!group?.isCorrection || items.some((it) => !!it?.isCorrection);
        const groupIsHighPriority =
            !!group?.isHighPriority || items.some((it) => !!it?.isHighPriority);

        return {
            id: group?.bookingId ?? bookingId,
            bookingId: group?.bookingId ?? bookingId,
            isCorrection: groupIsCorrection,
            isHighPriority: groupIsHighPriority,
            totals: {
                totalToBill: Number(group?.totalToBill ?? 0),
                totalToPay: Number(group?.totalToPay ?? 0),
            },
            items: items.map((it) => ({
                id: it?.id,
                transactionDate: it?.transactionDate ?? null,
                startDate: it?.startDate ?? null,
                endDate: it?.endDate ?? null,
                expenseType: it?.expenseType ?? it?.type ?? '',
                billAmount: Number(it?.billAmount ?? 0),
                amount: Number(it?.billAmount ?? 0),
                isCorrection: !!it?.isCorrection,
            })),
        };
    });

    // ---------------- Header fields and totals ----------------
    const amount = Number(netInvoiceAmount ?? totalInvoiceAmount ?? 0);

    // ---------------- Normalize & DEDUPE invoice issues ----------------
    const issues = dedupeNormalizedIssues(invoiceIssues);
    const issuesCount = issues.length;
    const hasAnyErrorIssue = issues.some((i) => i.level === 'error');
    const hasAnyWarningIssue = issues.some((i) => i.level === 'warning');

    // ---------------- UI helpers moved from InvoiceItemContainer ----------------

    // 1) Display “type” (with RT tag) based on invoiceType + any corrections in children
    const computeDisplayType = () => {
        const map = {
            TimeOnly: 'Timesheet',
            ExpenseOnly: 'Expense',
            TimeAndExpense: 'Timesheet & Expense',
            Deposits: 'Deposits',
            PermFees: 'Perm Fee',
            CreditMemos: 'Credit Memo',
            FinanceFees: 'Finance Fee',
        };
        const base = map[invoiceType] || '';
        if (!base) return '';
        const hasRTFromTimesheets = uiTimesheets.some((t) => t?.isCorrection);
        const hasRTFromExpenses = expensesGroups.some((g) => g?.isCorrection);
        return hasRTFromTimesheets || hasRTFromExpenses ? `${base}, RT` : base;
    };

    const displayType = computeDisplayType();

    // 2) Booking numbers as badges: [{ id, text: "BKG-123" }]
    const bookingNumberBadges = (() => {
        const ids = bookingIds;
        if (ids == null) return [{ id: 'unknown', text: 'Unknown' }];
        const list = Array.isArray(ids) ? ids : [ids];
        return [...new Set(list)].map((id) => ({ id: String(id), text: `BKG-${id}` }));
    })();

    // 3) Combined items list for the collapse (timesheet/expense)
    const items = (() => {
        const key = String(invoiceType || '').toLowerCase();
        const tsItems = uiTimesheets.map((ts) => ({ type: 'timesheet', id: ts.id, data: ts }));
        const expItems = expensesGroups
            .filter((exp) => (exp?.items?.length ?? 0) > 0 && (exp?.totals?.totalToBill ?? 0) > 0)
            .map((exp) => ({ type: 'expense', id: exp.id, data: exp }));

        switch (key) {
            case 'timeonly':
                return tsItems;
            case 'expenseonly':
                return expItems;
            case 'timeandexpense':
            default:
                return [...tsItems, ...expItems];
        }
    })();

    return {
        // selection/context ids
        bookingId,
        clientId: Number(customerId ?? 0) || null,

        // list header fields
        clientName,
        customerId,
        invoiceNumber,
        clinicianName,
        bookingIds,
        invoiceDate,
        accountingDate,
        // keep API value for logic
        statusCode: rawStatus,
        // show this in the UI pill/column
        status: displayStatus,
        invoiceType,
        amount,
        totalTax: Number(totalTax ?? 0),
        totalDiscount: Number(totalDiscount ?? 0),
        amountDue: amountDue != null ? Number(amountDue) : null,
        isPreInvoice: !!isPreInvoice,
        isCorrection: !!isCorrection,
        isHighPriority: !!isHighPriority,

        // flags + normalized issues
        hasErrors: !!hasErrors,
        hasWarnings: !!hasWarnings,
        issues, // normalized & de-duplicated
        issuesCount, // unique count

        selectable: selectable ?? true,
        numberOfLocations,
        collectorName,
        collectorSfId,

        // children for expanders / selection context
        timesheets: uiTimesheets,
        deposits: uiDeposits,
        expenses: expensesGroups, // grouped
        expensesGroups,

        // moved-from-row convenience
        displayType,
        bookingNumberBadges,
        items,

        // optional derived flags
        _derived: {
            hasAnyErrorIssue,
            hasAnyWarningIssue,
        },

        // for pdf
        fileName,
    };
}
